import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, LockKeyhole, Mail, ShieldCheck, Sparkles } from "lucide-react";

import {
  AuthDivider,
  AuthField,
  AuthFootnote,
  AuthHeading,
  AuthPasswordField,
  AuthPrimaryButton,
  GoogleSignInButton,
} from "./auth-primitives";
import { authFormWidthClass, authLinkClass } from "./auth-tokens";
import {
  DEFAULT_SIGNIN_COPY,
  DEFAULT_AUTH_LEGAL_COPY,
  type SigninFormCopy,
} from "./auth-copy";

export interface SigninFormProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  onNavigateToSignup?: () => void;
  onNavigateToForgotPassword: () => void;
  onGoogleSignIn?: () => void;
  googleLoading?: boolean;
  variant?: "default" | "admin";
  logoSrc?: string;
  /**
   * Translated labels, merged over the English defaults.
   *
   * Injected rather than read from i18next so this package stays free of any
   * locale assumption — see the note in `auth-copy.ts`. The admin variant
   * ignores it entirely.
   */
  copy?: Partial<SigninFormCopy>;
}

export const SigninForm = ({
  onSubmit,
  onNavigateToSignup,
  onNavigateToForgotPassword,
  onGoogleSignIn,
  googleLoading = false,
  variant = "default",
  logoSrc,
  copy,
}: SigninFormProps) => {
  const c = { ...DEFAULT_SIGNIN_COPY, ...copy };
  const legal = { ...DEFAULT_AUTH_LEGAL_COPY, ...c.legal };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = variant === "admin";

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await onSubmit(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : c.genericError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={authFormWidthClass}>
      <AuthHeading
        icon={
          isAdmin ? (
            <ShieldCheck className="h-6 w-6 text-primary" />
          ) : (
            <img src={logoSrc || "/icon.png"} alt="" className="h-9 w-9 object-contain" />
          )
        }
        title={isAdmin ? "Admin Portal" : c.title}
        description={
          isAdmin
            ? "Sign in to manage topics, vocabulary and members."
            : c.description
        }
      />

      <motion.form
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
        onSubmit={handleAuth}
        className="mt-7 space-y-4"
      >
        <AuthField
          name="email"
          label={c.emailLabel}
          icon={<Mail className="h-[18px] w-[18px]" />}
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={isAdmin ? "admin@example.com" : c.emailPlaceholder}
          autoComplete="email"
          required
          error={error}
        />

        <AuthPasswordField
          name="password"
          value={password}
          onChange={setPassword}
          label={c.passwordLabel}
          required
          error={null}
          action={
            <button
              type="button"
              onClick={onNavigateToForgotPassword}
              className={`text-[13px] ${authLinkClass}`}
            >
              {c.forgotPassword}
            </button>
          }
          showPasswordLabel={c.showPasswordLabel}
          hidePasswordLabel={c.hidePasswordLabel}
        />

        <div className="pt-1">
          <AuthPrimaryButton loading={loading} loadingLabel={isAdmin ? "Signing in…" : c.submitting}>
            {isAdmin ? (
              <>
                <ShieldCheck className="h-4 w-4" />
                Sign in to admin
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {c.submit}
                <ArrowRight className="h-4 w-4 opacity-70 transition-transform duration-200 group-hover:translate-x-0.5" />
              </>
            )}
          </AuthPrimaryButton>
        </div>

        {onGoogleSignIn && !isAdmin ? (
          <>
            <AuthDivider label={legal.orContinueWith} />
            <GoogleSignInButton loading={googleLoading} onClick={onGoogleSignIn} label={c.continueWithGoogle} />
          </>
        ) : null}
      </motion.form>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="mt-6 space-y-3"
      >
        {isAdmin ? (
          <div className="flex items-start gap-2.5 rounded-xl border border-stone-200/80 bg-stone-50/70 p-3 dark:border-stone-800 dark:bg-stone-800/40">
            <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-stone-400" />
            <p className="text-xs leading-relaxed text-stone-500 dark:text-stone-400">
              This area is restricted to authorised administrators. Every sign-in attempt is logged.
            </p>
          </div>
        ) : (
          <>
            {onNavigateToSignup ? (
              <p className="text-center text-sm text-stone-500 dark:text-stone-400">
                {c.newHere}{" "}
                <button type="button" className={authLinkClass} onClick={onNavigateToSignup}>
                  {c.createAccount}
                </button>
              </p>
            ) : null}
            <AuthFootnote>
              {legal.termsPrefix}{" "}
              <a href="/terms" className="underline underline-offset-2 hover:text-stone-600">
                {legal.termsLink}
              </a>{" "}
              {legal.andPrefix}{" "}
              <a href="/privacy" className="underline underline-offset-2 hover:text-stone-600">
                {legal.privacyLink}
              </a>
              .
            </AuthFootnote>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default SigninForm;